import type { ReactNode } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Heading,
    UnorderedList,
    ListItem,
    OrderedList,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Divider,
    Box,
    Link as ChakraLink,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
    INVOICE_REASON_FLAG_CONFIG,
    NUMERIC_REASON_BADGE_SCHEME,
    type InvoiceReasonFlagKey,
} from '../../components/invoice/invoiceReasonFlagConfig';
import { InvoiceReasonFlagBadge } from '../../components/invoice/InvoiceReasonFlagBadge';
import { getNexHttpReasonBadgeCopy } from '../../utils/nexHttpReasonBadge';
import { useGetSettings } from '../../hooks/useGetSettings';
import { getInvoiceNexIdentifierUi, getNexReconciliationIdentifierModeFromSettings } from '../../utils/invoiceNexIdentifierUi';
import { useIsOverflowing } from '../../hooks/useIsOverflowing';

export interface PartsInvoicingUserGuideModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const REASON_BADGE_ORDER: InvoiceReasonFlagKey[] = [
    'ro',
    'nex',
    'math',
    'dupe',
    'vendor',
    'specialVendor',
    'locationMismatch',
    'stale',
    'price',
    'discount',
    'qty',
    'total',
];

/** Very short copy for the user guide (full detail lives in tooltips on the invoice list). */
const REASON_BADGE_BLURB: Record<InvoiceReasonFlagKey, string> = {
    ro: 'Repair order missing or not found in Nexsyis.',
    nex: 'One or more lines still need a Nexsyis PO match.',
    math: 'Line math or invoice totals do not reconcile.',
    dupe: 'Already submitted in Yaxle for this vendor/invoice #/type, or Nexsyis 406. Correct the invoice number if this is a different document, then submit.',
    vendor: 'Invoice supplier does not match the Nexsyis vendor.',
    specialVendor: 'Resolved supplier is on your flagged-vendor list—auto-submit paused for review.',
    locationMismatch: 'Matched RO/PO is at a different shop than the uploader’s assigned location.',
    stale: 'Matched NEX folder parts are more than one year old—confirm before submitting.',
    price: 'Unit or list price disagrees with Nexsyis.',
    discount: 'Line discount (list vs buy) is below the expected discount for insurer, vendor, and state.',
    qty: 'Quantity disagrees with Nexsyis.',
    total: 'Line or extended amount rules disagree with Nexsyis.',
};

const ReasonBadgeRow = ({ label, colorScheme, children }: { label: string; colorScheme: string; children: ReactNode }) => (
    <HStack align="flex-start" spacing={3} flexWrap="wrap">
        <Box flexShrink={0} pt="2px" minW="108px">
            <InvoiceReasonFlagBadge label={label} colorScheme={colorScheme} />
        </Box>
        <Text fontSize="sm" color="gray.600" lineHeight="tall" flex="1" minW="220px">
            {children}
        </Text>
    </HStack>
);

const PartsInvoicingUserGuideModal = ({ isOpen, onClose }: PartsInvoicingUserGuideModalProps) => {
    const { data: orgSettings } = useGetSettings();
    const idUi = getInvoiceNexIdentifierUi(getNexReconciliationIdentifierModeFromSettings(orgSettings));
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent maxH="90vh">
                <ModalHeader
                    bg="gray.50"
                    borderTopRadius="md"
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                    fontSize="lg"
                    fontWeight="semibold"
                    color="gray.800"
                    pr={10}
                >
                    Parts Invoicing — how it works
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody ref={bodyRef} minH={0} py={5}>
                    <VStack align="stretch" spacing={5}>
                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                Why Yaxle invoicing exists
                            </Heading>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall">
                                Shops receive many parts invoices (and credits or returns) that must line up with repair
                                orders (ROs) and purchase orders in Nexsyis. Retyping lines, finding PO lines, and posting
                                payables by hand is slow and easy to get wrong.
                            </Text>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mt={2}>
                                <Text as="span" fontWeight="semibold" color="gray.700">
                                    Yaxle makes this easier
                                </Text>{' '}
                                by letting you photograph or upload a PDF of an invoice, reading the document
                                automatically, comparing it to Nexsyis for that RO, surfacing mismatches, and—when
                                everything checks out—sending the payable or return slip to Nexsyis so you avoid
                                re-keying every line.
                            </Text>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                What the tool does
                            </Heading>
                            <OrderedList fontSize="sm" color="gray.600" lineHeight="tall" spacing={2} pl={1}>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Upload
                                    </Text>{' '}
                                    — Send one PDF, or one or more photos of the same invoice, for the correct shop
                                    location (and invoice category where the app asks for it).
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Extract
                                    </Text>{' '}
                                    — Yaxle uses document intelligence (OCR) to pull vendor text, invoice number,
                                    dates, RO when visible, line items, freight, tax, discounts, and totals.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Reconcile
                                    </Text>{' '}
                                    — It loads Nexsyis PO data for the RO, matches invoice lines to Nexsyis lines, then
                                    checks quantities, prices, amounts, math, and totals against your organization
                                    rules.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Submit or flag
                                    </Text>{' '}
                                    — If there are no blocking issues and Nexsyis accepts the request, the invoice can
                                    post automatically. If something does not line up—or your org marks certain vendors
                                    for review—the invoice is flagged until someone fixes or confirms it.
                                </ListItem>
                            </OrderedList>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                Taking and uploading photos
                            </Heading>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={2}>
                                These practices improve extraction and matching.
                            </Text>
                            <UnorderedList fontSize="sm" color="gray.600" lineHeight="tall" spacing={2} pl={1}>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Same document, correct order
                                    </Text>{' '}
                                    — Every image must be from the same invoice. Upload pages in order (page 1, then 2,
                                    …). Wrong pages or order breaks lines and totals.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Framing and orientation
                                    </Text>{' '}
                                    — Fill the frame: crop or rescan so the paper fills most of the picture with minimal
                                    empty margin. Keep each photo right-side up, not sideways or upside down.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Lighting and sharpness (mobile)
                                    </Text>{' '}
                                    — The mobile app may reject images that are too blurry, too dark, or too bright. Use
                                    steady hands, tap to focus on text, and avoid glare on glossy invoices.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        PDF vs photos (web)
                                    </Text>{' '}
                                    — Upload either a set of images or a single PDF in one submission—not both.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Location and invoice type
                                    </Text>{' '}
                                    — Pick the correct shop location. Choose the invoice type that matches the paper
                                    (received, credited/returned, return slip, or sublet).
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Repair order (RO)
                                    </Text>{' '}
                                    — Enter the six-digit RO when you know it. If you skip it where allowed, Yaxle
                                    may read the RO from the document; if it cannot, you will need to correct the RO or
                                    refresh Nexsyis data before submission.
                                </ListItem>
                            </UnorderedList>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                What gets flagged
                            </Heading>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                                The invoice list shows{' '}
                                <Text as="span" fontWeight="medium" color="gray.700">
                                    Reason
                                </Text>{' '}
                                badges when something needs attention. Below is what each badge means (hover a badge on
                                the list for full tooltips).
                            </Text>
                            <VStack align="stretch" spacing={3} pl={0}>
                                {REASON_BADGE_ORDER.map((key) => {
                                    const c = INVOICE_REASON_FLAG_CONFIG[key];
                                    const blurb = key === 'ro' ? idUi.reasonFlagUserGuideBlurb : REASON_BADGE_BLURB[key];
                                    const badgeLabel = key === 'ro' ? idUi.reasonBadgeLabel : c.label;
                                    return (
                                        <ReasonBadgeRow key={key} label={badgeLabel} colorScheme={c.colorScheme}>
                                            {blurb}
                                        </ReasonBadgeRow>
                                    );
                                })}
                                <ReasonBadgeRow
                                    label={getNexHttpReasonBadgeCopy(400).label}
                                    colorScheme={NUMERIC_REASON_BADGE_SCHEME}
                                >
                                    Nexsyis rejected a submit attempt (HTTP error). The gray badge summarizes the error
                                    type on the list; open the invoice for full detail.{' '}
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        These are not reconciliation issues you can clear inside Yaxle
                                    </Text>
                                    —try re-uploading a corrected invoice, fix the underlying problem in Nexsyis, or
                                    contact Yaxle for help.
                                </ReasonBadgeRow>
                            </VStack>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mt={4}>
                                Other issues (no extracted lines, restocking on returns, page total vs lines, etc.) can
                                still flag an invoice without adding every badge above; use invoice detail to fix them.
                                Nexsyis submit failures (including duplicate 406) are driven by Nexsyis’s rules, not the
                                in-app match workflow—use re-upload or Nexsyis/partner support paths above when those
                                badges appear.
                            </Text>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                How to reconcile flags
                            </Heading>
                            <OrderedList fontSize="sm" color="gray.600" lineHeight="tall" spacing={2} pl={1}>
                                <ListItem>
                                    On invoice detail, work through flags in Key Identifiers, Line Items, totals, and
                                    the Key Reconciliations tab until Submit is available. Submitted and read-only
                                    invoices cannot be edited.
                                </ListItem>
                                <ListItem>
                                    Confirm RO and header fields; use{' '}
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Refresh Nexsyis data
                                    </Text>{' '}
                                    when the RO is correct but PO lines or quantities in Nexsyis have changed since the
                                    last fetch.
                                </ListItem>
                                <ListItem>
                                    Resolve vendor mismatches using vendor aliases (where configured) or the match
                                    tools in the invoice detail UI.
                                </ListItem>
                                <ListItem>
                                    Open each flagged line, compare invoice vs Nexsyis, then match to the correct PO
                                    line, adjust values if the invoice is authoritative, or unmatch lines that should
                                    not post.
                                </ListItem>
                                <ListItem>
                                    For special-vendor-only flags, follow your shop policy on who may review and
                                    submit.
                                </ListItem>
                                <ListItem>
                                    Save your changes; use refresh when you need a fresh Nexsyis snapshot after DMS-side
                                    updates.
                                </ListItem>
                            </OrderedList>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                Invoice settings
                            </Heading>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={2}>
                                Shop-level options live under{' '}
                                <ChakraLink
                                    as={RouterLink}
                                    to="/settings#section-invoicing"
                                    color="blue.600"
                                    fontWeight="medium"
                                    _hover={{ textDecoration: 'underline' }}
                                >
                                    Settings → Invoicing
                                </ChakraLink>
                                . They affect future uploads and Nexsyis submissions.
                            </Text>
                            <UnorderedList fontSize="sm" color="gray.600" lineHeight="tall" spacing={2} pl={1}>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Email invoice inbox
                                    </Text>{' '}
                                    — Optional typed addresses on <Text as="span" fontFamily="mono">@bills.partbay.ai</Text>.
                                    When enabled under Settings → Invoicing, your shop gets four addresses:
                                    <Text as="span" fontFamily="mono"> received-…</Text> for received invoices,{' '}
                                    <Text as="span" fontFamily="mono">credited-…</Text> for credited invoices,{' '}
                                    <Text as="span" fontFamily="mono">return-slips-…</Text> for return slips, and{' '}
                                    <Text as="span" fontFamily="mono">sublets-…</Text> for sublet invoices. Forward
                                    vendor emails (with a PDF or image attached) to the matching address to create
                                    uploads automatically.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Freight as line item
                                    </Text>{' '}
                                    — Whether freight posts to Nexsyis as a distribution line vs a header amount.
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Amount mismatch thresholds
                                    </Text>{' '}
                                    — Cutoffs for flagging received vs credited lines when invoice and Nexsyis
                                    extended totals disagree (Y% if either total is under $X, otherwise $Z off).
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Vendor flagging rules
                                    </Text>{' '}
                                    — Per-vendor overrides of unmatched-submit and mismatch flagging.
                                    Require manual review pauses auto-submit even when the invoice matches
                                    (Special Vendor badge).
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Invoice → Nexsyis vendor aliases
                                    </Text>{' '}
                                    — Map text seen on invoices to the exact Nexsyis vendor name so PO matching is
                                    consistent.
                                </ListItem>
                            </UnorderedList>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={2} color="gray.800">
                                What is sent to Nexsyis
                            </Heading>
                            <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={2}>
                                Yaxle sends structured data to Nexsyis integration APIs. The payload depends on
                                invoice type:
                            </Text>
                            <UnorderedList fontSize="sm" color="gray.600" lineHeight="tall" spacing={2} pl={1}>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Received or credited invoices (AP payables)
                                    </Text>{' '}
                                    — Header fields such as invoice number, date, freight, discount, tax, total,
                                    invoice type (regular vs credit memo), and a hosted link to the invoice image;
                                    plus distribution lines tied to the RO folder and Nexsyis PO lines (quantities,
                                    list and unit costs, extensions, and related flags as shown in Yaxle before
                                    submit).
                                </ListItem>
                                <ListItem>
                                    <Text as="span" fontWeight="medium" color="gray.700">
                                        Return slips
                                    </Text>{' '}
                                    — A dedicated return-slip update: RO folder, PO header, the PO line numbers being
                                    returned, and the invoice image URL—not the same shape as a standard AP invoice
                                    post.
                                </ListItem>
                            </UnorderedList>
                        </Box>

                        <Divider />

                        <Box>
                            <Heading as="h3" size="sm" mb={3} color="gray.800">
                                Quick checklist before upload
                            </Heading>
                            <Table size="sm" variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Check</Th>
                                        <Th>Why it matters</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr>
                                        <Td fontWeight="medium" color="gray.700">
                                            Same invoice, ordered pages
                                        </Td>
                                        <Td color="gray.600">Correct lines and totals</Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="medium" color="gray.700">
                                            Upright, full frame, good light
                                        </Td>
                                        <Td color="gray.600">OCR accuracy; mobile quality checks</Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="medium" color="gray.700">
                                            Correct location and invoice type
                                        </Td>
                                        <Td color="gray.600">Right shop and Nexsyis posting path</Td>
                                    </Tr>
                                    <Tr>
                                        <Td fontWeight="medium" color="gray.700">
                                            RO known or clearly printed
                                        </Td>
                                        <Td color="gray.600">Matching and automatic submission</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button size="action" variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PartsInvoicingUserGuideModal;
